'use client';

import { useEffect, useState } from 'react';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  isWithinRadius,
  matchCityAlias,
  type LocationMatchMethod,
  type LocationMatchResult,
  type LocationMatchTarget,
} from 'utils/locationMatch';

const BROWSER_GEO_TIMEOUT_MS = 5_000;

const resolved = (
  matched: boolean,
  method: LocationMatchMethod,
): LocationMatchResult => ({
  status: 'resolved',
  matched,
  method,
});

/** Cascade: IP city alias → IP coords radius → browser geolocation. */
export function useLocationMatch(
  target: LocationMatchTarget,
): LocationMatchResult {
  const { city, latitude, longitude, region, detectedRegionFromGeoIP } =
    useRegionLocale();

  const cityAliases = target.cityAliases;
  const centreLat = target.latitude;
  const centreLon = target.longitude;
  const radiusKm = target.radiusKm;
  const browserGeoCountries = target.browserGeoCountries;

  const [result, setResult] = useState<LocationMatchResult>(() => {
    if (matchCityAlias(city, cityAliases)) {
      return resolved(true, 'city');
    }
    if (
      isWithinRadius(
        latitude,
        longitude,
        { latitude: centreLat, longitude: centreLon },
        radiusKm,
      )
    ) {
      return resolved(true, 'ip_coords');
    }
    return { status: 'pending', matched: false, method: 'none' };
  });

  useEffect(() => {
    const centre = { latitude: centreLat, longitude: centreLon };

    if (matchCityAlias(city, cityAliases)) {
      setResult(resolved(true, 'city'));
      return;
    }

    if (isWithinRadius(latitude, longitude, centre, radiusKm)) {
      setResult(resolved(true, 'ip_coords'));
      return;
    }

    const visitorCountry = (
      detectedRegionFromGeoIP ||
      region ||
      ''
    ).toUpperCase();
    const allowBrowserGeo =
      !!browserGeoCountries?.length &&
      browserGeoCountries.some((c) => c.toUpperCase() === visitorCountry);

    if (
      !allowBrowserGeo ||
      typeof navigator === 'undefined' ||
      !navigator.geolocation
    ) {
      setResult(resolved(false, 'none'));
      return;
    }

    let cancelled = false;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) return;
        const matched = isWithinRadius(
          position.coords.latitude,
          position.coords.longitude,
          centre,
          radiusKm,
        );
        setResult(resolved(matched, matched ? 'browser_geo' : 'none'));
      },
      () => {
        if (cancelled) return;
        setResult(resolved(false, 'none'));
      },
      {
        timeout: BROWSER_GEO_TIMEOUT_MS,
        maximumAge: 600_000,
      },
    );

    return () => {
      cancelled = true;
    };
  }, [
    city,
    latitude,
    longitude,
    region,
    detectedRegionFromGeoIP,
    cityAliases,
    centreLat,
    centreLon,
    radiusKm,
    browserGeoCountries,
  ]);

  return result;
}
